import { useState } from "react";
import ServiceCard from "./ServiceCard";
import { MoveLeft, MoveRight } from "lucide-react";
import { useServicesData } from "../../hooks/useServicesData";

export default function OurServices() {
  const [activeIndex, setActiveIndex] = useState(1);
  const { data } = useServicesData();
  const services = data.ourServices.services;

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % services.length);
  };

  const prevSlide = () => {
    setActiveIndex((prev) => (prev - 1 + services.length) % services.length);
  };

  const getCardStyle = (index: number) => {
    const totalCards = services.length;
    const angleStep = (2 * Math.PI) / totalCards; // 360 degrees divided by number of cards
    const currentAngle = index * angleStep;
    const activeAngle = activeIndex * angleStep;
    const diff = index - activeIndex;

    // Calculate position on the circle
    const radius = 350; // Increased radius for larger circle layout
    const x = Math.sin(currentAngle - activeAngle) * radius;
    const z = Math.cos(currentAngle - activeAngle) * radius - radius; // Center at origin

    if (diff === 0) {
      // Center card - fully visible and prominent
      return {
        transform: `translateX(${x}px) translateZ(${z}px) rotateY(0deg) scale(1)`,
        opacity: 1,
        zIndex: 5,
        filter: "drop-shadow(0 20px 40px rgba(0, 0, 0, 0.6))",
      };
    } else {
      // All other cards positioned around the circle
      const rotationY = (currentAngle - activeAngle) * (180 / Math.PI); // Convert radians to degrees
      const scale = (Math.abs(z + radius) / (2 * radius)) * 0.2 + 0.8; // Increased minimum scale for better visibility

      return {
        transform: `translateX(${x}px) translateZ(${z}px) rotateY(${rotationY}deg) scale(${scale})`,
        zIndex:
          Math.abs(z + radius) < 200 ? 3 : Math.abs(z + radius) < 400 ? 2 : 1,
        filter: `drop-shadow(0 ${Math.abs(z + radius) / 10}px ${
          Math.abs(z + radius) / 5
        }px rgba(0, 0, 0, 0.3))`,
      };
    }
  };

  return (
    <>
      <h2 className="text-white text-center text-4xl md:text-6xl font-medium mb-2 tracking-wider uppercase py-8 md:py-16 bg-[#D9D9D9]/10">
        {data.ourServices.title}
      </h2>
      <div className="pb-20 overflow-hidden relative">
        {/* Background Grid Pattern */}
        <div className="absolute inset-0 opacity-30">
          {/* Primary Grid Layer */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `
            linear-gradient(rgba(59, 130, 246, 0.15) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59, 130, 246, 0.15) 1px, transparent 1px)
          `,
              backgroundSize: "60px 60px",
              transform: "perspective(1500px) rotateX(65deg)",
              transformOrigin: "center top",
            }}
          />

          {/* Secondary Grid Layer for more depth */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `
            linear-gradient(rgba(59, 130, 246, 0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59, 130, 246, 0.08) 1px, transparent 1px)
          `,
              backgroundSize: "120px 120px",
              transform: "perspective(2000px) rotateX(70deg)",
              transformOrigin: "center top",
            }}
          />

          {/* Ambient Light Effect */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-900/5 to-transparent" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center my-8 md:my-16">
            <h3 className="text-white text-3xl md:text-6xl font-medium mb-8">
              {data.ourServices.subtitle.split(' ').slice(0, 3).join(' ')}
              <br />
              {data.ourServices.subtitle.split(' ').slice(3).join(' ')}
            </h3>
          </div>

          {/* 3D Carousel Container */}
          <div className="relative max-w-6xl lg:max-w-7xl mx-auto">
            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 z-10 text-white/60 hover:text-white transition-colors duration-200 bg-white/10 rounded-full p-2 md:p-6"
              style={{ transform: "translateY(-50%)" }}
            >
              <MoveLeft />
            </button>

            <button
              onClick={nextSlide}
              className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 z-10 text-white/60 hover:text-white transition-colors duration-200 bg-white/10 rounded-full p-2 md:p-6"
              style={{ transform: "translateY(-50%)" }}
            >
              <MoveRight />
            </button>

            {/* 3D Carousel */}
            <div
              className="relative h-[500px] md:h-[600px] lg:h-[700px] flex items-center justify-center"
              style={{
                perspective: "2000px",
                perspectiveOrigin: "center center",
                transformStyle: "preserve-3d",
              }}
            >
              {/* 3D Space Container */}
              <div
                className="relative w-full h-full"
                style={{
                  transformStyle: "preserve-3d",
                  transform: "rotateX(-20deg) translateY(-40px)",
                }}
              >
                {services.map((service, index) => (
                  <div
                    key={index}
                    className="absolute w-[320px] md:w-[360px] cursor-pointer"
                    style={{
                      ...getCardStyle(index),
                      transition: "all 1.2s cubic-bezier(0.4, 0.0, 0.2, 1)",
                      transformStyle: "preserve-3d",
                      left: "50%",
                      top: "50%",
                      marginLeft: "-160px",
                      marginTop: "-100px",
                    }}
                    onClick={() => setActiveIndex(index)}
                  >
                    <ServiceCard
                      title={service.title}
                      description={service.description}
                      color={service.color}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation dots */}
          <div className="flex justify-center mt-12 space-x-2 md:space-x-3">
            {services.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`w-8 md:w-12 h-1 rounded-full transition-all duration-200 ${
                  index === activeIndex ? "bg-white" : "bg-white/30"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
